import type { LegalDocPair } from '../types'

/**
 * Términos de inteligencia artificial.
 *
 * Cubre las funciones que generan sitios y ayudan a editarlos. Dice lo que el
 * código hace: la solicitud y el contexto necesario viajan a un proveedor de IA
 * (hoy Gemini de Google), hay cupos por plan y no se entrena ningún modelo con
 * el contenido del cliente. Lo que hace el proveedor con los datos lo rigen sus
 * propios términos, y así se dice: no se promete lo que no controlamos.
 */
export const AI: LegalDocPair = {
  en: {
    title: 'AI Terms',
    summary: 'How véltica’s AI features work, who owns what they generate, and what you should check.',
    intro: [
      'These AI Terms are part of the [Terms of Service](doc:terms) and apply when you use a feature of the Platform that uses artificial intelligence, such as generating a site, writing or rewriting content, or the editing assistant (the “AI Features”).',
    ],
    sections: [
      {
        title: 'How the AI Features work',
        subs: [
          {
            title: 'What we send',
            blocks: [
              'When you use an AI Feature, we send your request (your “Input”), and the parts of your Site and the business information needed to answer it, to a third-party AI provider, today Google’s Gemini API, and we show you what comes back (the “Output”). We may change providers.',
              'Photos you give the site assistant are Input too. Each one is sent to the provider once, when you upload it, to get a short description of what it shows (for example, “a plate of tacos” or “a logo”). We keep that description with the image and use it to place the photo on your Site and to suggest it later in the editor.',
            ],
          },
          {
            title: 'Do not send sensitive data',
            blocks: [
              'Do not put in your Input passwords, payment card numbers, government identifiers, health information or other sensitive personal data, or anything you do not have the right to share. Follow the [Acceptable Use Policy](doc:aup) in your Input; we may refuse or block requests that violate it.',
            ],
          },
          {
            title: 'How the provider handles data',
            blocks: [
              'The provider processes Inputs and Outputs under its own terms and privacy commitments, which we choose with care but do not control. We do not use your Customer Content, Inputs or Outputs to train AI models. See the [Privacy Policy](doc:privacy) and the [Subprocessors](doc:subprocessors) page.',
            ],
          },
          {
            title: 'Allowances',
            blocks: [
              'AI Features are subject to allowances that depend on your plan, shown in the Platform. There are two: a monthly allowance for the assistants (in the editor and the dashboard), which resets on the first day of each month (UTC), and a number of AI generations of sites and pages. When an allowance runs out, the AI Features that use it stop working until it resets or you change plan; the rest of the Platform keeps working. If a request made in several steps has already started, we may let it finish even if it goes slightly over.',
              'A page generated with AI counts as one generation when it is generated. A site generated with AI counts when you create the site from it; up to three site generations in any 30 days that you do not turn into a site are free tries, and from the fourth, each one counts when it is generated. Deleting a site or a page does not give a generation back.',
              'We record, for each AI request, which feature it was, how many tokens it used and its estimated cost, so we can apply the allowances and prevent abuse. We may limit or pause the AI Features to prevent abuse or when a provider is unavailable, and free accounts registered with a temporary or disposable email address do not include AI Features.',
            ],
          },
        ],
      },
      {
        title: 'Output',
        subs: [
          {
            title: 'Ownership and use',
            blocks: [
              'As between you and us, and to the extent the law allows, you may use the Output for your Sites and your business, and we claim no ownership of it. We do not promise that the law recognizes any copyright in Output.',
            ],
          },
          {
            title: 'It may not be unique or accurate',
            blocks: [
              'Output is generated automatically. It may be inaccurate, incomplete, out of date, biased, or similar to what the AI produces for other people, and it may resemble third-party material. It is not legal, financial, medical or other professional advice.',
            ],
          },
          {
            title: 'You are responsible for what you publish',
            blocks: [
              'You must review Output before you publish it. You are responsible for its accuracy, for having the rights to use it, for complying with the law, including any duty to tell people that content was generated with AI, and for what you claim about your business, products, prices and results. We are not responsible for Output you publish.',
            ],
          },
        ],
      },
      {
        title: 'Disclaimers',
        blocks: [
          'THE AI FEATURES AND THEIR OUTPUT ARE PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND. THE DISCLAIMERS, LIMITS OF LIABILITY AND INDEMNIFICATION IN THE TERMS OF SERVICE APPLY TO THEM. WE MAY CHANGE, LIMIT OR DISCONTINUE AI FEATURES AT ANY TIME.',
        ],
      },
    ],
  },

  es: {
    title: 'Términos de inteligencia artificial',
    summary: 'Cómo funcionan las funciones de IA de véltica, de quién es lo que generan y qué debes revisar.',
    intro: [
      'Estos Términos de inteligencia artificial forman parte de los [Términos de servicio](doc:terms) y aplican cuando usas una función de la Plataforma que emplea inteligencia artificial, como generar un sitio, escribir o reescribir contenido, o el asistente de edición (las «Funciones de IA»).',
    ],
    sections: [
      {
        title: 'Cómo funcionan las Funciones de IA',
        subs: [
          {
            title: 'Qué enviamos',
            blocks: [
              'Cuando usas una Función de IA, enviamos tu solicitud (tu «Entrada»), y las partes de tu Sitio y de la información de tu negocio necesarias para responderla, a un proveedor de IA de terceros, hoy la API Gemini de Google, y te mostramos lo que devuelve (la «Salida»). Podemos cambiar de proveedor.',
              'Las fotos que le das al asistente de sitios también son Entrada. Cada una se envía una vez al proveedor, al subirla, para obtener una descripción breve de lo que muestra (por ejemplo, «un plato de tacos» o «un logo»). Guardamos esa descripción junto a la imagen y la usamos para colocar la foto en tu Sitio y para sugerirla después en el editor.',
            ],
          },
          {
            title: 'No envíes datos sensibles',
            blocks: [
              'No pongas en tu Entrada contraseñas, números de tarjeta de pago, identificadores oficiales, información de salud u otros datos personales sensibles, ni nada que no tengas derecho a compartir. Cumple la [Política de uso aceptable](doc:aup) en tu Entrada; podemos rechazar o bloquear solicitudes que la incumplan.',
            ],
          },
          {
            title: 'Cómo trata los datos el proveedor',
            blocks: [
              'El proveedor trata las Entradas y Salidas según sus propios términos y compromisos de privacidad, que elegimos con cuidado pero no controlamos. No usamos tu Contenido del cliente, Entradas ni Salidas para entrenar modelos de IA. Consulta la [Política de privacidad](doc:privacy) y la página de [Subprocesadores](doc:subprocessors).',
            ],
          },
          {
            title: 'Cupos',
            blocks: [
              'Las Funciones de IA están sujetas a cupos que dependen de tu plan, que se muestran en la Plataforma. Son dos: un cupo mensual para los asistentes (en el editor y en el panel), que se reinicia el primer día de cada mes (UTC), y un número de generaciones de sitios y páginas con IA. Cuando un cupo se agota, las Funciones de IA que lo usan dejan de funcionar hasta que se reinicie o cambies de plan; el resto de la Plataforma sigue funcionando. Si una solicitud hecha en varios pasos ya empezó, podemos dejar que termine aunque se pase un poco.',
              'Una página generada con IA cuenta como una generación al generarse. Un sitio generado con IA cuenta cuando creas el sitio a partir de él; hasta tres generaciones de sitio en cualquier periodo de 30 días que no conviertas en un sitio son pruebas gratuitas, y desde la cuarta, cada una cuenta al generarse. Borrar un sitio o una página no devuelve la generación.',
              'Registramos, por cada solicitud a la IA, qué función se usó, cuántos tokens consumió y su costo estimado, para aplicar los cupos y prevenir abusos. Podemos limitar o pausar las Funciones de IA para prevenir abusos o cuando un proveedor no esté disponible, y las cuentas gratuitas registradas con un correo temporal o desechable no incluyen Funciones de IA.',
            ],
          },
        ],
      },
      {
        title: 'La Salida',
        subs: [
          {
            title: 'Titularidad y uso',
            blocks: [
              'Entre tú y nosotros, y en la medida en que la ley lo permita, puedes usar la Salida para tus Sitios y tu negocio, y no reclamamos su propiedad. No prometemos que la ley reconozca derechos de autor sobre la Salida.',
            ],
          },
          {
            title: 'Puede no ser única ni exacta',
            blocks: [
              'La Salida se genera automáticamente. Puede ser inexacta, incompleta, desactualizada, sesgada o parecida a lo que la IA produce para otras personas, y puede parecerse a material de terceros. No es asesoría legal, financiera, médica ni de otro tipo profesional.',
            ],
          },
          {
            title: 'Eres responsable de lo que publicas',
            blocks: [
              'Debes revisar la Salida antes de publicarla. Eres responsable de su exactitud, de tener derecho a usarla, de cumplir la ley, incluida cualquier obligación de informar de que un contenido se generó con IA, y de lo que afirmes sobre tu negocio, productos, precios y resultados. No respondemos de la Salida que publiques.',
            ],
          },
        ],
      },
      {
        title: 'Exclusión de garantías',
        blocks: [
          'LAS FUNCIONES DE IA Y SU SALIDA SE OFRECEN «TAL CUAL», SIN GARANTÍA DE NINGÚN TIPO. LES APLICAN LA EXCLUSIÓN DE GARANTÍAS, LOS LÍMITES DE RESPONSABILIDAD Y LA INDEMNIZACIÓN DE LOS TÉRMINOS DE SERVICIO. PODEMOS CAMBIAR, LIMITAR O DEJAR DE OFRECER LAS FUNCIONES DE IA EN CUALQUIER MOMENTO.',
        ],
      },
    ],
  },
}
